import { observer } from 'mobx-react-lite';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useClientSearchController } from '@/controllers/clients/useClientSearchController';
import type { AccountSearch } from '@/models/clients/types';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/layout/PageHeader';
import { ClientSearchInput } from './ClientSearchInput';
import { ClientsRecentList } from './ClientsRecentList';
import { ClientsSearchResults } from './ClientsSearchResults';

export const ClientsPage = observer(function ClientsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    store,
    setSearch,
    setStatusFilter,
    setAccountTypeFilter,
    setSelectOpen,
    setTypeSelectOpen,
    selectClient,
  } = useClientSearchController(searchParams.get('q') ?? '');

  const hasSearch = store.search.trim().length > 0;

  function handleSetSearch(value: string) {
    setSearch(value);
    if (value.trim()) {
      setSearchParams({ q: value }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }

  function handleClientClick(client: AccountSearch) {
    selectClient(client);
    navigate(`/clients/${client.id_account}`, {
      state: { idAccount: client.id_account, accountSearch: { ...client } },
    });
  }

  return (
    <div className='min-h-screen bg-background flex flex-col'>
      <PageHeader
        activeTab='clients'
        onLogoClick={() => {
          handleSetSearch('');
          navigate('/clients');
        }}
      />
      <div
        className={cn(
          'px-6 flex-1 flex flex-col items-center',
          'transition-[padding-top,padding-bottom] duration-420 ease-out',
          hasSearch ? 'pt-4 pb-6' : 'pt-[28vh] pb-24'
        )}
      >
        <ClientSearchInput
          value={store.search}
          onChange={handleSetSearch}
          isLoading={store.isLoading}
        />
        <ClientsRecentList
          recents={store.recents}
          hasSearch={hasSearch}
          onClientClick={handleClientClick}
        />
        <AnimatePresence>
          {hasSearch && (
            <ClientsSearchResults
              store={store}
              setStatusFilter={setStatusFilter}
              setAccountTypeFilter={setAccountTypeFilter}
              setSelectOpen={setSelectOpen}
              setTypeSelectOpen={setTypeSelectOpen}
              onClientClick={handleClientClick}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
});
